import { CompositeChart } from '@mantine/charts'
import type { ComboboxData } from '@mantine/core'
import { ActionIcon, Group, Paper, Select, Stack, Tabs, Text, Title } from '@mantine/core'
import {
  IconChevronLeft,
  IconChevronRight,
  IconEyeExclamation,
  IconMessage,
  IconPencil,
  IconRuler2,
  IconRulerMeasure,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { ResponsiveContainer } from 'recharts'

import { StatInfoGraphic } from '@/components/StatInfoGraphic'
import { useExplicitDateRange } from '@/hooks/useExplicitDateRange'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetContentModuleResponse } from '@/services/api/api.types'
import { selectStyles } from '@/styles/Chart.css'
import {
  canNavigateToPreviousPeriod,
  cmLedgerMetrics,
  conversationsSeries,
  formatChartDate,
  metricsSelectableCms,
  shareOfDayConversations,
} from '@/utils/analytics'

import { ConversationByDateTooltip } from './ConversationByDateTooltip'
import { NoDataAvailable } from './NoDataAvailable'
import TabChartWrapper from './TabChartWrapper'

const TabKeys = {
  conversationLineChart: 'conversationLineChart',
} as const

export default function ContentModuleInsights() {
  const { api, authenticationStore } = useGlobalState()
  const { user, isSponsorAdmin } = authenticationStore

  const [selectedCm, setSelectedCm] = useState<GetContentModuleResponse | null>(null)
  const [activeTab, setActiveTab] = useState<string | null>(TabKeys.conversationLineChart)
  const [backPeriod, setBackPeriod] = useState(0)

  const { data: sponsorInfo } = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
    staleTime: 5 * 60 * 1000,
  })

  const cmQuery = useQuery({
    queryKey: ['cm', 'list'],
    queryFn: () => api.getContentModules(),
    initialData: [],
    refetchInterval: 7 * 1000,
  })

  const dateRange = useExplicitDateRange(backPeriod, sponsorInfo?.createdAt)

  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics', 'cmCreditsByDate', selectedCm?.taskId, backPeriod, dateRange.startDate, dateRange.endDate],
    queryFn: () =>
      api.getCmCreditsByDate({
        cm_id: selectedCm?.taskId ?? '',
        ...dateRange.analyticsParams,
      }),
    enabled: !!selectedCm?.taskId,
  })

  const handlePreviousPeriod = () => setBackPeriod(prev => prev - 1)
  const handleNextPeriod = () => setBackPeriod(prev => prev + 1)

  const canGoPrevious = canNavigateToPreviousPeriod(backPeriod, sponsorInfo?.createdAt)
  const canGoNext = backPeriod < 0

  const selectableCms = metricsSelectableCms(cmQuery.data, { isSponsorAdmin, userId: user?.userId })

  const cmOptions: ComboboxData = selectableCms.map(({ taskId, name }) => ({
    value: taskId ?? '',
    label: name ?? '',
    disabled: !taskId,
  }))

  const currentInSelectable = selectedCm?.taskId != null && selectableCms.some(cm => cm.taskId === selectedCm.taskId)
  const fallbackCm = selectableCms[0] ?? null
  if (!currentInSelectable && fallbackCm !== selectedCm) {
    setSelectedCm(fallbackCm)
  }

  const metrics = useMemo(() => cmLedgerMetrics(data), [data])

  return (
    <Stack>
      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Group justify="space-between" mb="md" align="top">
          <Select
            data={cmOptions}
            label="Content Module"
            placeholder="Select a content module"
            value={selectedCm?.taskId ?? null}
            onChange={value => {
              const cm = cmQuery.data.find(cm => cm.taskId === value)
              setSelectedCm(cm ?? null)
            }}
            miw={300}
            classNames={selectStyles}
          />
          <Stack align="flex-end" gap="md">
            <Group gap="xs" align="center">
              <ActionIcon
                variant="subtle"
                onClick={handlePreviousPeriod}
                disabled={!canGoPrevious}
                aria-label="Previous period">
                <IconChevronLeft size={20} />
              </ActionIcon>

              <Text size="sm" c="dimmed" style={{ minWidth: 180, textAlign: 'center' }}>
                {formatChartDate(dateRange.startDate, true)} — {formatChartDate(dateRange.endDate, true)}
              </Text>

              <ActionIcon variant="subtle" onClick={handleNextPeriod} disabled={!canGoNext} aria-label="Next period">
                <IconChevronRight size={20} />
              </ActionIcon>
            </Group>

            <Group gap="xl">
              <StatInfoGraphic
                icon={IconEyeExclamation}
                label="Unique Visitors"
                value={metrics.uniqueVisitors}
                tooltipText="Members who used this content module, counted once for each day they used it"
              />

              <StatInfoGraphic
                icon={IconMessage}
                label="Conversations"
                value={metrics.conversations}
                tooltipText="Conversations in this content module, counted once for each day they were active"
              />

              <StatInfoGraphic
                icon={IconPencil}
                label="Messages"
                value={metrics.messages}
                tooltipText="Total messages sent in this content module during this period"
              />

              <StatInfoGraphic
                icon={IconRuler2}
                label="Average Length"
                value={Math.round(metrics.averageLength)}
                tooltipText="Average messages per conversation on each day it was active, from members and the Sidekick"
              />

              <StatInfoGraphic
                icon={IconRulerMeasure}
                label="Max Length"
                value={metrics.maxLength}
                tooltipText="Most messages in this content module on a single day"
              />
            </Group>
          </Stack>
        </Group>
      </Paper>

      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value={TabKeys.conversationLineChart}>Conversations</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value={TabKeys.conversationLineChart}>
            <Group justify="space-between" align="baseline">
              <Title order={6} mb="md" pt="md">
                Number of Conversations over Time
              </Title>
            </Group>

            <TabChartWrapper isActive={activeTab === TabKeys.conversationLineChart} isLoading={isLoading} error={error}>
              {metrics.hasActivity ? (
                <ResponsiveContainer width="100%" height={300}>
                  <CompositeChart
                    data={metrics.chartData}
                    dataKey="date"
                    curveType="linear"
                    series={conversationsSeries}
                    xAxisProps={{
                      tickFormatter: (date: string) => {
                        return formatChartDate(date)
                      },
                      angle: -45,
                      textAnchor: 'end',
                      interval: 0,
                    }}
                    tooltipProps={{
                      content: ({ payload, label }) => {
                        if (!payload || payload.length === 0) return null

                        return (
                          <ConversationByDateTooltip
                            label={label}
                            percentage={shareOfDayConversations(payload)}
                            payload={payload}
                          />
                        )
                      },
                    }}
                  />
                </ResponsiveContainer>
              ) : (
                <NoDataAvailable />
              )}
            </TabChartWrapper>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </Stack>
  )
}
